import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { LocalStorageService } from 'ngx-store';

//Model
import { DamageReport, RepairRequest } from '../model/maintenance.model';
import { Url } from '../model/users.model';
import { Fleet } from '../model/fleet.model';
import { RegoRegistration } from '../model/fleet.model';
import { Repair } from '../model/fleet.model';
import { Tyres } from '../model/fleet.model';
import { Fuel } from '../model/fleet.model';
import { Insurance } from '../model/fleet.model';
import { Service } from '../model/fleet.model';
import { OtherCost } from '../model/fleet.model';
import { Page } from '../model/pagination';
import { FleetCompliance } from 'src/app/model/fleet_compliance.model';

@Injectable({
    providedIn: 'root'
})
export class FleetService {
    private url: Url = this.storage.get('url');
      
    // Http Headers
    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        })
    }

    constructor(public http: HttpClient, private storage: LocalStorageService) { 
    }

    /**
     * Get fleet resources.
     * 
     * This is an alternative to the default {@link FleetService.getFleet getFleets} method.
     * 
     * @param params HTTP query parameters.
     * @returns Paginated fleet resources.
     * @see {@link https://github.com/logiscor/ceres-laravel8-restapi/commit/578f6ac75b80c905242b89adafca2e624f381a31 Sample usage}.
     */
    getFleets2(params?: HttpParams | { [param: string]: string | string[] }) {
        return this.http.get<Page<Fleet>>(`${this.url.rest}/tms/fleets`, { params })
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getUnarchiveFleets(): Observable<{fleets: Fleet[]}> { 
        return this.http.get<{fleets: Fleet[]}>(this.url.rest + '/tms/getFleets', this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )  
    }

	getFleets(): Observable<{fleets: Fleet[]}> { 
        let user = this.storage.get('user');

        if(user.role_id == 9){
            return this.http.get<{fleets: Fleet[]}>(this.url.rest + '/tms/getFleets?user_role='+user.role_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )  
        }
        else{
            return this.http.get<{fleets: Fleet[]}>(this.url.rest + '/tms/getFleets', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )  
        }
    }

    getFleet(id:number): Observable<Fleet> { 
        return this.http.get<Fleet>(this.url.rest + '/tms/getFleet/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getFleetIdNumbers(): Observable<Fleet[]> { 
        return this.http.get<Fleet[]>(this.url.rest + '/tms/getFleetIdNumbers', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }


    getRegoRegistrationByFleetId(id:number): Observable<RegoRegistration> { 
        return this.http.get<RegoRegistration>(this.url.rest + '/tms/fleetdetails/getRegoRegByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getRepairsByFleetId(id:number): Observable<Repair> { 
        return this.http.get<Repair>(this.url.rest + '/tms/fleetdetails/getRepairsByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getTyresByFleetId(id:number): Observable<Tyres> { 
        return this.http.get<Tyres>(this.url.rest + '/tms/fleetdetails/getTyresByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getFuelsByFleetId(id:number): Observable<Fuel> { 
        return this.http.get<Fuel>(this.url.rest + '/tms/fleetdetails/getFuelsByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getFleetInsurancesByFleetId(id:number): Observable<Insurance> { 
        return this.http.get<Insurance>(this.url.rest + '/tms/fleetdetails/getFleetInsurancesByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getServicesByFleetId(id:number): Observable<Service> { 
        return this.http.get<Service>(this.url.rest + '/tms/fleetdetails/getServicesByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getDamageReportsByFleetId(id:number): Observable<DamageReport> { 
        return this.http.get<DamageReport>(this.url.rest + '/tms/fleetdetails/getDamageReportsByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getRepairRequestsByFleetId(id:number): Observable<RepairRequest> { 
        return this.http.get<RepairRequest>(this.url.rest + '/tms/fleetdetails/getRepairRequestsByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getOtherCostsByFleetId(id:number): Observable<OtherCost> { 
        return this.http.get<OtherCost>(this.url.rest + '/tms/fleetdetails/getOtherCostsByFleetId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    deleteFleet(id: number) {
        return this.http.delete(this.url.rest + '/tms/deleteFleet/' + id);
    }

    archiveFleet(id: number){
        let user = this.storage.get('user');

        let payload = {
            fleet_id: id,
            user_id: user.id
        };

        return this.http.post(this.url.rest + '/tms/archiveFleet', payload, this.httpOptions);
    }

    unarchiveFleet(id: number){
        let user = this.storage.get('user');

        let payload = {
            fleet_id: id,
            user_id: user.id
        };

        return this.http.post(this.url.rest + '/tms/unarchiveFleet', payload, this.httpOptions);
    }

    createFleet(fleet:Fleet): Observable<Fleet> {    
        return this.http.post<Fleet>(this.url.rest + '/tms/createFleet', fleet , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }


    updateFleet(fleet:Fleet): Observable<Fleet> {
        return this.http.put<any>(this.url.rest + '/tms/updateFleet', fleet , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getFleetCustomers(): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/getCustomersCompanyName', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getFleetTypes(): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/getFleetTypes', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getNonTrailerRegos(): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/getNonTrailerRegos', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    enableFleet(id: number): Observable<Fleet> {
        return this.http.get<Fleet>(this.url.rest + '/tms/enableFleet/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    disableFleet(id: number): Observable<Fleet> {
        return this.http.get<Fleet>(this.url.rest + '/tms/disableFleet/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    activeFleet(id: number, value: number, active:number): Observable<Fleet> {
        return this.http.get<Fleet>(this.url.rest + '/tms/enableFleet/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getTotalFleetJobs(id:number): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/getTotalFleetJobs/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getTotalFleetSummaryPanel(id, filter, start_date = '', end_date = ''): Observable<any[]> { 
        var param = '';
        if (filter == 'between') {
            param = '/' + start_date + '/' + end_date; 
        }

        return this.http.get<any[]>(this.url.rest + '/tms/getTotalFleetSummaryPanel/' + id + '/' + filter + param, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getPhasesTotalDistance(fleet:Fleet): Observable<any> {
        return this.http.post<any>(this.url.rest + '/tms/getPhasesTotalDistance' , fleet , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getInsuranceYearSummary(id:number): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/fleetdetails/getInsuranceYearSummary/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getOtherCostYearSummary(id:number): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/fleetdetails/getOtherCostYearSummary/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getFuelYearSummary(id:number): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/fleetdetails/getFuelYearSummary/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }
    
    getRepairYearSummary(id:number): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/fleetdetails/getRepairYearSummary/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getRegistrationYearSummary(id:number): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/fleetdetails/getRegistrationYearSummary/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getTyreYearSummary(id:number): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/fleetdetails/getTyreYearSummary/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getServiceYearSummary(id:number): Observable<any[]> { 
        return this.http.get<any[]>(this.url.rest + '/tms/fleetdetails/getServiceYearSummary/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    


    }

    getValuesByRange(rangeData: any): Observable<any> {
        console.log(rangeData);
        return this.http.post<any>(this.url.rest + '/tms/getValuesByRange' , rangeData , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }
    
    getFleetCostSummary(): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getFleetCostSummary' , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    updateFleetExpiry(expiry): Observable<any> {
        return this.http.put<any>(this.url.rest + '/tms/updateFleetExpiry', expiry , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getFleetsForFieldDropdown(): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getFleetsForFieldDropdown' , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getFleetNomination(id:number): Observable<FleetCompliance> { 
        return this.http.get<FleetCompliance>(this.url.rest + '/tms/getComplainceNominationById/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }
    
    createFleetNomination(fleet_nomination:FleetCompliance): Observable<FleetCompliance> {    
        return this.http.post<FleetCompliance>(this.url.rest + '/tms/createFleetNomination', fleet_nomination , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    updateFleetNomination(fleet_nomination:FleetCompliance): Observable<FleetCompliance> {    
        return this.http.post<FleetCompliance>(this.url.rest + '/tms/updateFleetNomination', fleet_nomination , this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    deleteFleetNomination(id: number) {
        return this.http.delete(this.url.rest + '/tms/deleteFleetNomination/' + id);
      }

      
    getComplainceNominationQuestionbyId(id:number): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getComplainceNominationQuestionbyId/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

      
    private extractData(res: Response) {
    	let body = res.json();
    	console.log(body);
	    if(body)
            return body || {};
        else
            return null;
    }

    // Error handling
    handleError(error) {
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
            // Get client-side error
            errorMessage = error.error.message;
        } else {
            // Get server-side error
            errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }
        console.log(errorMessage);
        return throwError(errorMessage);
    }

}